import type { AdditionalIncome } from "@porchpass/ui";
import {
  DomainIncomeType,
  type DomainIncomeType as DomainIncomeTypeValue,
} from "@porchpass/api-client";
import type { GenericIncomeFormData } from "src/types/income";
import { fieldErrorsAfterSubmitRecord } from "src/forms/fieldErrorsAfterSubmit";

const UI_TO_DLA_FIELD: Partial<
  Record<keyof AdditionalIncome, keyof GenericIncomeFormData>
> = {
  monthlyAmount: "amount",
  typeOther: "income_type_other",
  startMonth: "from_month",
  startYear: "from_year",
  endMonth: "to_month",
  endYear: "to_year",
};

function parseChildrenAges(value?: string): number[] | undefined {
  if (!value?.trim()) return undefined;
  const ages = value
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "" && !Number.isNaN(Number(part)))
    .map(Number);
  return ages.length > 0 ? ages : undefined;
}

function formatChildrenAges(ages?: number[]): string | undefined {
  if (!ages?.length) return undefined;
  return ages.join(",");
}

/** Incomes saved before the Ongoing switch was tracked are ongoing when they have no end date. */
export function isIncomeOngoing(
  dto: Pick<GenericIncomeFormData, "is_ongoing" | "to_month" | "to_year">,
): boolean {
  return dto.is_ongoing ?? (!dto.to_month && !dto.to_year);
}

export function toUiAdditionalIncome(
  dto: GenericIncomeFormData,
): AdditionalIncome {
  const isOngoing = isIncomeOngoing(dto);

  return {
    id: dto.id,
    type: (dto.income_type ?? "") as DomainIncomeTypeValue,
    monthlyAmount:
      dto.amount != null && dto.amount !== "" ? Number(dto.amount) : undefined,
    typeOther: dto.income_type_other,
    startMonth: dto.from_month,
    startYear: dto.from_year,
    endMonth: dto.to_month,
    endYear: dto.to_year,
    isOngoing,
    childrenAges: parseChildrenAges(dto.children_ages),
  };
}

export function fromUiAdditionalIncomePatch(
  patch: Partial<AdditionalIncome>,
): Partial<GenericIncomeFormData> {
  const mapped: Partial<GenericIncomeFormData> = {};

  for (const [uiKey, value] of Object.entries(patch)) {
    if (value === undefined) continue;

    if (uiKey === "childrenAges") {
      mapped.children_ages = formatChildrenAges(value as number[]);
      continue;
    }

    if (uiKey === "isOngoing") {
      mapped.is_ongoing = value as boolean;
      if (value) {
        mapped.to_month = "";
        mapped.to_year = "";
      }
      continue;
    }

    const dlaKey = UI_TO_DLA_FIELD[uiKey as keyof AdditionalIncome];
    if (!dlaKey) continue;

    if (uiKey === "monthlyAmount") {
      mapped.amount = String(value);
    } else if (dlaKey === "income_type_other") {
      mapped.income_type_other = value as string;
    } else if (
      dlaKey === "from_month" ||
      dlaKey === "from_year" ||
      dlaKey === "to_month" ||
      dlaKey === "to_year"
    ) {
      mapped[dlaKey] = value as string;
    }
  }

  return mapped;
}

export function additionalIncomeErrorsAfterSubmit<
  T extends {
    amount?: { message?: string };
    income_type_other?: { message?: string };
    from_month?: { message?: string };
    from_year?: { message?: string };
    to_month?: { message?: string };
    to_year?: { message?: string };
    children_ages?: { message?: string };
  },
>(formErrors: T, hasAttemptedFormSubmission: boolean): Record<string, string> {
  return fieldErrorsAfterSubmitRecord(
    {
      monthlyAmount: formErrors.amount?.message,
      typeOther: formErrors.income_type_other?.message,
      startMonth: formErrors.from_month?.message,
      startYear: formErrors.from_year?.message,
      endMonth: formErrors.to_month?.message,
      endYear: formErrors.to_year?.message,
      childrenAges: formErrors.children_ages?.message,
    },
    hasAttemptedFormSubmission,
  );
}

export function incomeTypeToDomain(
  type: DomainIncomeTypeValue,
): DomainIncomeTypeValue {
  const map: Record<DomainIncomeTypeValue, DomainIncomeTypeValue> = {
    BASE: DomainIncomeType.IncomeBase,
    BONUS: DomainIncomeType.IncomeBonus,
    COMMISSION: DomainIncomeType.IncomeCommission,
    OVERTIME: DomainIncomeType.IncomeOvertime,
    SSI: DomainIncomeType.IncomeSSI,
    DISABILITY: DomainIncomeType.IncomeDisability,
    SSI_FOR_DEPENDENTS: DomainIncomeType.IncomeSSIForDependents,
    VA_DISABILITY: DomainIncomeType.IncomeVADisability,
    RETIREMENT: DomainIncomeType.IncomeRetirement,
    ALIMONY: DomainIncomeType.IncomeAlimony,
    CHILD_SUPPORT: DomainIncomeType.IncomeChildSupport,
    RENTAL: DomainIncomeType.IncomeRental,
    WIC: DomainIncomeType.IncomeWIC,
    TANF: DomainIncomeType.IncomeTANF,
    SNAP: DomainIncomeType.IncomeSNAP,
    OTHER: DomainIncomeType.IncomeOther,
  };

  return map[type];
}
